import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { v4 as uuidv4 } from 'uuid';
import type { TerraUIMessage } from '@terra/shared-dto-chat-types';
import {
  EMPTY_SKILL_DRAFT,
  type SkillDraft,
  useSkillDraftsStore,
} from '../../store/skillDraftsStore';
import { readSkillDraftUpdates } from './skillDraftUpdateParser';
import {
  hasInProgressManageCustomRolesToolInMessages,
  hasUnconsumedCustomRoleUpdatesInMessages,
  readOrderedCustomRoleUpdates,
  readObjectUpdates,
} from './objectUpdateParser';
import { stageObjectUpdate } from './objectDraftRegistry';
import {
  CREDENTIAL_OBJECT_KIND,
  CUSTOM_ROLE_OBJECT_KIND,
  isChatEditableObjectKind,
} from '../../utils/chatEditableObjectUtils';
import { useCustomRoleUpdateQueueStore } from '../../store/customRoleUpdateQueueStore';
import { useCredentialUpdateQueueStore } from '../../store/credentialUpdateQueueStore';
import { useCopilotFindingEditProcessingStore } from '../../store/copilotFindingEditProcessingStore';
import { isObjectUpdateConsumed } from '../../store/consumedObjectUpdatesStore';
import { collectUpdateSignalToolCompletions } from './collectUpdateSignalToolCompletions';
import { SIGNAL_DETAIL_QUERY_KEY } from '../../hooks/api/useUpdateSignal';
import { createSkillMention } from '../../utils/skillMentionUtils';
import { useChatTabsStore } from '../../store/chatTabsStore';
import type { useMainContentTabs } from '../../store/layoutStore';

function releaseCopilotObjectUpdateStateForChat(targetChatId: string) {
  useCustomRoleUpdateQueueStore
    .getState()
    .releaseCustomRoleUpdateStateForChat(targetChatId);
  useCredentialUpdateQueueStore
    .getState()
    .releaseCredentialUpdateStateForChat(targetChatId);
  useCopilotFindingEditProcessingStore
    .getState()
    .releaseProcessingFindingEditForChat(targetChatId);
}

export function useCopilotMessageUpdateEffects({
  chatId,
  tabId,
  appTargetId,
  skillDraftId,
  messages,
  openTab,
  tSkillEditor,
}: {
  chatId: string;
  tabId: string;
  appTargetId: string | undefined;
  skillDraftId: string | undefined;
  messages: TerraUIMessage[];
  openTab: ReturnType<typeof useMainContentTabs>['openTab'];
  tSkillEditor: (key: string) => string;
}) {
  const queryClient = useQueryClient();
  const { updateChatTab } = useChatTabsStore();
  const patchSkillDraft = useSkillDraftsStore((state) => state.patchDraft);
  const setSkillDraft = useSkillDraftsStore((state) => state.setDraft);

  const appliedSkillDraftUpdateIdsRef = useRef<Set<string>>(new Set());
  const appliedObjectUpdateIdsRef = useRef<Set<string>>(new Set());
  const previousSkillDraftIdRef = useRef<string | undefined>(skillDraftId);
  const lastScannedSkillDraftMessageCountRef = useRef(0);
  const lastScannedObjectUpdateMessageCountRef = useRef(0);
  const hydratedConsumedObjectUpdatesRef = useRef(false);
  const lastScannedUpdateSignalRefreshMessageCountRef = useRef(0);
  const hydratedUpdateSignalRefreshesRef = useRef(false);
  const refreshedUpdateSignalToolCallIdsRef = useRef(new Set<string>());
  const previousChatIdRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (previousSkillDraftIdRef.current !== skillDraftId) {
      const previousSkillDraftId = previousSkillDraftIdRef.current;

      previousSkillDraftIdRef.current = skillDraftId;
      if (previousSkillDraftId && skillDraftId) {
        appliedSkillDraftUpdateIdsRef.current.clear();
        lastScannedSkillDraftMessageCountRef.current = 0;
      }
    }

    const lastScannedMessageCount =
      lastScannedSkillDraftMessageCountRef.current > messages.length
        ? 0
        : lastScannedSkillDraftMessageCountRef.current;
    const newMessages = messages.slice(lastScannedMessageCount);
    const latestMessage = messages.at(-1);
    const messagesToScan =
      newMessages.length > 0
        ? newMessages
        : latestMessage
          ? [latestMessage]
          : [];

    lastScannedSkillDraftMessageCountRef.current = messages.length;
    if (messagesToScan.length === 0) {
      return;
    }

    const updates = readSkillDraftUpdates({ messages: messagesToScan });
    let targetSkillDraftId = skillDraftId;

    for (const update of updates) {
      if (appliedSkillDraftUpdateIdsRef.current.has(update.id)) {
        continue;
      }

      appliedSkillDraftUpdateIdsRef.current.add(update.id);
      const patch: Partial<SkillDraft> = {};

      if (update.slug !== undefined) {
        patch.slug = update.slug;
      }

      if (update.name !== undefined) {
        patch.name = update.name;
      }

      if (update.description !== undefined) {
        patch.description = update.description;
      }

      if (update.body !== undefined) {
        patch.body = update.body;
      }

      if (Object.keys(patch).length === 0) {
        continue;
      }

      if (!targetSkillDraftId) {
        targetSkillDraftId = `create-${uuidv4()}`;
        const initialDraft = {
          ...EMPTY_SKILL_DRAFT,
          ...patch,
        };

        setSkillDraft(targetSkillDraftId, initialDraft);
        const tabTitle =
          initialDraft.name || initialDraft.slug || tSkillEditor('createTitle');
        const skillMention = createSkillMention({
          skillDraftId: targetSkillDraftId,
          mode: 'create',
          label: tabTitle,
          fallbackLabel: tSkillEditor('createTitle'),
        });
        const existingChatTab = useChatTabsStore
          .getState()
          .chatTabs.find((chatTab) => chatTab.tabId === tabId);
        const mentionsWithoutGeneratedDraft =
          existingChatTab?.mentions?.filter(
            (mention) => mention.id !== skillMention.id,
          ) ?? [];

        updateChatTab(tabId, {
          skillDraftId: targetSkillDraftId,
          mentions: [skillMention, ...mentionsWithoutGeneratedDraft],
        });
        openTab({
          id: `skill-editor-${targetSkillDraftId}`,
          title: tabTitle,
          type: 'skill-editor',
          data: { skillDraftId: targetSkillDraftId, mode: 'create' },
        });
        continue;
      }

      patchSkillDraft(targetSkillDraftId, patch);
    }
  }, [
    messages,
    openTab,
    patchSkillDraft,
    setSkillDraft,
    skillDraftId,
    tSkillEditor,
    tabId,
    updateChatTab,
  ]);

  useEffect(() => {
    hydratedConsumedObjectUpdatesRef.current = false;
    appliedObjectUpdateIdsRef.current.clear();
    lastScannedObjectUpdateMessageCountRef.current = 0;
    hydratedUpdateSignalRefreshesRef.current = false;
    refreshedUpdateSignalToolCallIdsRef.current.clear();
    lastScannedUpdateSignalRefreshMessageCountRef.current = 0;

    const previousChatId = previousChatIdRef.current;

    previousChatIdRef.current = chatId;

    if (previousChatId !== undefined && previousChatId !== chatId) {
      releaseCopilotObjectUpdateStateForChat(previousChatId);
    }
  }, [chatId]);

  useEffect(() => {
    return () => {
      releaseCopilotObjectUpdateStateForChat(chatId);
    };
  }, [chatId]);

  useEffect(() => {
    if (hydratedConsumedObjectUpdatesRef.current || messages.length === 0) {
      return;
    }

    hydratedConsumedObjectUpdatesRef.current = true;
    for (const update of readObjectUpdates({ messages })) {
      if (isObjectUpdateConsumed(update.id)) {
        appliedObjectUpdateIdsRef.current.add(update.id);
      }
    }
  }, [messages]);

  useEffect(() => {
    if (!hydratedUpdateSignalRefreshesRef.current) {
      if (messages.length === 0) {
        return;
      }

      hydratedUpdateSignalRefreshesRef.current = true;
      for (const completion of collectUpdateSignalToolCompletions({
        messages,
      })) {
        refreshedUpdateSignalToolCallIdsRef.current.add(completion.toolCallId);
      }

      lastScannedUpdateSignalRefreshMessageCountRef.current = messages.length;

      return;
    }

    const lastScannedMessageCount =
      lastScannedUpdateSignalRefreshMessageCountRef.current > messages.length
        ? 0
        : lastScannedUpdateSignalRefreshMessageCountRef.current;
    const newMessages = messages.slice(lastScannedMessageCount);
    const latestMessage = messages.at(-1);
    const messagesToScan =
      newMessages.length > 0
        ? newMessages
        : latestMessage
          ? [latestMessage]
          : [];

    lastScannedUpdateSignalRefreshMessageCountRef.current = messages.length;

    for (const completion of collectUpdateSignalToolCompletions({
      messages: messagesToScan,
    })) {
      if (
        refreshedUpdateSignalToolCallIdsRef.current.has(completion.toolCallId)
      ) {
        continue;
      }

      refreshedUpdateSignalToolCallIdsRef.current.add(completion.toolCallId);

      if (completion.signalId) {
        void queryClient.invalidateQueries({
          queryKey: [SIGNAL_DETAIL_QUERY_KEY, completion.signalId],
        });
      }

      if (appTargetId) {
        void queryClient.invalidateQueries({
          queryKey: ['endpoint-signals-paginated', appTargetId],
          exact: false,
        });
        void queryClient.invalidateQueries({
          queryKey: ['signal-counts', appTargetId],
          exact: false,
        });
        void queryClient.invalidateQueries({
          queryKey: ['ai-component-signal-counts', appTargetId],
          exact: false,
        });
        void queryClient.invalidateQueries({
          queryKey: ['signals-count-by-endpoint', appTargetId],
          exact: false,
        });
      }
    }
  }, [messages, queryClient, appTargetId]);

  useEffect(() => {
    const lastScannedMessageCount =
      lastScannedObjectUpdateMessageCountRef.current > messages.length
        ? 0
        : lastScannedObjectUpdateMessageCountRef.current;
    const newMessages = messages.slice(lastScannedMessageCount);
    const latestMessage = messages.at(-1);
    const messagesToScan =
      newMessages.length > 0
        ? newMessages
        : latestMessage
          ? [latestMessage]
          : [];

    lastScannedObjectUpdateMessageCountRef.current = messages.length;

    const shouldDeferCredentialActivation = () =>
      useCustomRoleUpdateQueueStore.getState().hasPendingCustomRoleWork() ||
      hasUnconsumedCustomRoleUpdatesInMessages({ messages }) ||
      hasInProgressManageCustomRolesToolInMessages({ messages });

    if (messagesToScan.length === 0) {
      if (!shouldDeferCredentialActivation()) {
        useCredentialUpdateQueueStore.getState().tryActivateCredentialUpdates();
      }

      return;
    }

    const updates = readObjectUpdates({ messages: messagesToScan });

    for (const update of readOrderedCustomRoleUpdates({ messages })) {
      if (update.objectKind !== CUSTOM_ROLE_OBJECT_KIND) {
        continue;
      }

      if (isObjectUpdateConsumed(update.id)) {
        appliedObjectUpdateIdsRef.current.add(update.id);
        continue;
      }

      appliedObjectUpdateIdsRef.current.add(update.id);
      useCustomRoleUpdateQueueStore.getState().enqueueCustomRoleUpdates({
        updates: [update],
        chatId,
      });
    }

    for (const update of updates) {
      if (
        appliedObjectUpdateIdsRef.current.has(update.id) ||
        isObjectUpdateConsumed(update.id)
      ) {
        continue;
      }

      appliedObjectUpdateIdsRef.current.add(update.id);
      if (!isChatEditableObjectKind(update.objectKind)) {
        continue;
      }

      if (update.objectKind === CUSTOM_ROLE_OBJECT_KIND) {
        continue;
      }

      if (update.objectKind === CREDENTIAL_OBJECT_KIND) {
        useCredentialUpdateQueueStore.getState().enqueueCredentialUpdates({
          updates: [update],
          chatId,
        });
        continue;
      }

      stageObjectUpdate({
        objectKind: update.objectKind,
        objectId: update.objectId,
        patch: update.patch,
        reason: update.reason,
        sourceUpdateId: update.id,
      });
    }

    useCustomRoleUpdateQueueStore.getState().tryActivateNextCustomRoleUpdate();

    if (!shouldDeferCredentialActivation()) {
      useCredentialUpdateQueueStore.getState().tryActivateCredentialUpdates();
    }
  }, [chatId, messages]);
}
